import { forwardRef } from 'react';
import { Divider } from '@material-ui/core';
import cx from 'classnames';
import { intersection } from 'lodash';
import PropTypes from 'prop-types';

import { CustomList } from 'src/components/CustomList';
import { ImageOverlayForm } from 'src/components/ImageOverlayForm';
import { ImageStyleList } from 'src/components/ImageStyleList';
import { ToneOfVoiceList } from 'src/components/ToneOfVoiceList';
import { GENERATION_IMPROVE_MODE } from 'src/data/generation';

import { ChangeImageOverlayItem } from './components/ChangeImageOverlayItem';
import { ChangeImageStyleItem } from './components/ChangeImageStyleItem';
import { ChangeTovListItem } from './components/ChangeTovListItem';
import { HeaderListItem } from './components/HeaderListItem';
import { InternalListItem } from './components/InternalListItem';
import { GetDataOverrideContextProvider } from './lib';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.oneOf(Object.values(GENERATION_IMPROVE_MODE))).isRequired,
  selectedParams: PropTypes.shape(),
  onSettingsClick: PropTypes.func,
  onSettingsPopupStateChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  ToneOfVoiceListProps: PropTypes.shape(ToneOfVoiceList.propTypes),
  ImageStyleListProps: PropTypes.shape(ImageStyleList.propTypes),
  ImageOverlayProps: PropTypes.shape(ImageOverlayForm.propTypes),
  isImageTool: PropTypes.bool,
  displayHeader: PropTypes.bool,
  hideHeaderTitle: PropTypes.bool,
  hideHeaderSettingsButton: PropTypes.bool,
  hasOverlayText: PropTypes.bool,
  getDataOverride: PropTypes.func,
  disabled: PropTypes.bool,
};
const defaultProps = {
  className: '',
  selectedParams: null,
  onSettingsClick: null,
  onSettingsPopupStateChange: null,
  ToneOfVoiceListProps: null,
  ImageStyleListProps: null,
  ImageOverlayProps: null,
  isImageTool: false,
  displayHeader: false,
  hideHeaderTitle: false,
  hideHeaderSettingsButton: false,
  hasOverlayText: false,
  getDataOverride: null,
  disabled: false,
};

export const GenerationImproveList = forwardRef(
  (
    {
      className,
      options,
      selectedParams,
      onSettingsClick,
      onSettingsPopupStateChange,
      onSubmit,
      ToneOfVoiceListProps,
      ImageStyleListProps,
      ImageOverlayProps,
      isImageTool,
      displayHeader,
      hideHeaderTitle,
      hideHeaderSettingsButton,
      hasOverlayText,
      getDataOverride,
      disabled,
      ...restProps
    },
    ref
  ) => {
    const styles = useStyles();

    const handleListClick = (event) => {
      event.stopPropagation(); // Prevents the menu from closing when clicking on the list
    };

    const handleListItemClick = (improveMode) => {
      onSubmit({ improveMode });
    };

    const handleTovSelect = (result) => {
      onSubmit({ improveMode: GENERATION_IMPROVE_MODE.changeTov, tov: result });
    };

    const handleSelectImageStyle = (result) => {
      onSubmit({
        improveMode: GENERATION_IMPROVE_MODE.changeImageStyle,
        imageStyleId: result?.id,
        imageStyleName: result?.name,
      });
    };

    const handleSubmitImageOverlay = () => {
      onSubmit({ improveMode: GENERATION_IMPROVE_MODE.changeImageOverlay });
    };

    const renderListItem = (mode) => (
      <InternalListItem
        className={styles.internalListItem}
        key={mode}
        mode={mode}
        onClick={handleListItemClick}
        selected={selectedParams?.improveMode === mode}
        disabled={disabled}
      />
    );

    return (
      <GetDataOverrideContextProvider value={getDataOverride}>
        <CustomList
          className={cx(styles.root, className)}
          ref={ref}
          onClick={handleListClick}
          data-testid="GenerationImproveList"
          {...restProps}
        >
          {displayHeader && (
            <>
              <HeaderListItem
                onSettingsClick={onSettingsClick}
                onSettingsPopupStateChange={onSettingsPopupStateChange}
                isImageTool={isImageTool}
                hideTitle={hideHeaderTitle}
                hideSettingsButton={hideHeaderSettingsButton}
                disabled={disabled}
              />

              <Divider className={styles.divider} />
            </>
          )}

          {intersection(options, [GENERATION_IMPROVE_MODE.doNotImprove]).map(renderListItem)}

          {isImageTool ? (
            <>
              {intersection(options, [
                // TODO: Uncomment when "image" performance improvement is supported by BE/AC
                // GENERATION_IMPROVE_MODE.improveImage,
              ]).map(renderListItem)}

              {options.includes(GENERATION_IMPROVE_MODE.changeImageOverlay) && (
                <ChangeImageOverlayItem
                  ImageOverlayProps={{
                    ...ImageOverlayProps,
                    onSubmit: handleSubmitImageOverlay,
                  }}
                  hasOverlayText={hasOverlayText}
                  selected={
                    selectedParams?.improveMode === GENERATION_IMPROVE_MODE.changeImageOverlay
                  }
                  disabled={disabled}
                />
              )}

              {options.includes(GENERATION_IMPROVE_MODE.changeImageStyle) && (
                <ChangeImageStyleItem
                  ImageStyleListProps={{
                    ...ImageStyleListProps,
                    onSelect: handleSelectImageStyle,
                  }}
                  selected={
                    selectedParams?.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle
                  }
                  disabled={disabled}
                />
              )}
            </>
          ) : (
            <>
              {intersection(options, [
                GENERATION_IMPROVE_MODE.continueWriting,
                GENERATION_IMPROVE_MODE.boostScoreEngaging,
                GENERATION_IMPROVE_MODE.boostScoreSimilar,
                GENERATION_IMPROVE_MODE.improveEngaging,
                GENERATION_IMPROVE_MODE.improveSimilar,
                GENERATION_IMPROVE_MODE.makeCasual,
                GENERATION_IMPROVE_MODE.makeLonger,
                GENERATION_IMPROVE_MODE.makeShorter,
              ]).map(renderListItem)}

              {options.includes(GENERATION_IMPROVE_MODE.changeTov) && (
                <ChangeTovListItem
                  ToneOfVoiceListProps={{
                    ...ToneOfVoiceListProps,
                    onSelect: handleTovSelect,
                  }}
                  selected={selectedParams?.improveMode === GENERATION_IMPROVE_MODE.changeTov}
                  disabled={disabled}
                />
              )}
            </>
          )}
        </CustomList>
      </GetDataOverrideContextProvider>
    );
  }
);

GenerationImproveList.displayName = 'GenerationImproveList';
GenerationImproveList.propTypes = propTypes;
GenerationImproveList.defaultProps = defaultProps;
